import React, { useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import PencilIcon from '../../svgs/PencilIcon';

import type { FontFamily, FontVariant } from '@src/types.ts/story';

type Props = {
  family: FontFamily;
  onDeleteVariant?: (variant: FontVariant) => void;
  onDeleteFamily?: (familyId: string) => void;
  onRenameFamily?: (familyId: string, newName: string) => void;
  /**
   * PLAT-11412 — workspace languages this family cannot display ("Japanese").
   * Empty/omitted renders nothing (full coverage, or pre-feature uploads
   * whose coverage is unknown).
   */
  uncoveredLanguages?: string[];
};

const FontFamilyCard = ({
  family,
  onDeleteVariant,
  onDeleteFamily,
  onRenameFamily,
  uncoveredLanguages,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(family.name);

  const activeVariants = family.variants.filter(v => !v.archivedAt);

  if (!activeVariants.length) return null;

  const handleRenameCommit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== family.name) {
      onRenameFamily?.(family.id, trimmed);
    } else {
      setEditName(family.name);
    }
    setIsEditing(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleRenameCommit();
    if (e.key === 'Escape') {
      setEditName(family.name);
      setIsEditing(false);
    }
  };

  const showOriginal =
    family.originalName && family.originalName !== family.name;

  return (
    <SCard>
      <SFamilyHeader>
        {isEditing ? (
          <SNameEditArea>
            <SNameInput
              autoFocus
              value={editName}
              maxLength={64}
              onChange={e => setEditName(e.target.value)}
              onBlur={handleRenameCommit}
              onKeyDown={handleRenameKeyDown}
            />
            {family.originalName && (
              <SOriginalName>Original: {family.originalName}</SOriginalName>
            )}
          </SNameEditArea>
        ) : (
          <SNameRow>
            <SFamilyName>{family.name}</SFamilyName>
            {showOriginal && (
              <SOriginalName>({family.originalName})</SOriginalName>
            )}
            {onRenameFamily && (
              <SIconBtn
                onClick={() => {
                  setEditName(family.name);
                  setIsEditing(true);
                }}
                title="Rename family"
              >
                <PencilIcon
                  width="12"
                  height="12"
                  strokeColor="var(--text-muted)"
                />
              </SIconBtn>
            )}
          </SNameRow>
        )}
        {onDeleteFamily && !isEditing && (
          <SIconBtn
            onClick={() => onDeleteFamily(family.id)}
            title="Delete family"
          >
            <DeleteIcon
              strokeColor="var(--text-muted)"
              width="14"
              height="14"
            />
          </SIconBtn>
        )}
      </SFamilyHeader>
      {uncoveredLanguages && uncoveredLanguages.length > 0 && (
        <SCoverageNote>
          Doesn't support {uncoveredLanguages.join(', ')}
        </SCoverageNote>
      )}
      <SWeightRow>
        {activeVariants.map(v => (
          <SWeightChip key={v.artifactId}>
            {/* Render each label in its own loaded face so weights AND widths
                (Compressed/Condensed/Extra Compressed) are visually distinct.
                useLoadBrandKitFonts registers every variant as a FontFace keyed
                by postScriptName; matching font-weight/style avoids the browser
                synthesizing faux-bold/italic. Inline style (not styled-component
                interpolation) keeps the dynamic font name injection-safe. */}
            <SWeightLabel
              style={{
                fontFamily: `"${v.postScriptName}", sans-serif`,
                fontWeight: v.weight,
                fontStyle: v.style,
              }}
            >
              {v.label}
            </SWeightLabel>
            {onDeleteVariant && (
              <SDeleteVariantBtn
                onClick={e => {
                  e.stopPropagation();
                  onDeleteVariant(v);
                }}
                title={`Remove ${v.label}`}
              >
                <DeleteIcon
                  strokeColor="var(--text-muted)"
                  width="10"
                  height="10"
                />
              </SDeleteVariantBtn>
            )}
          </SWeightChip>
        ))}
      </SWeightRow>
    </SCard>
  );
};

const SCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--surface-elevated);
  border-radius: 8px;
  padding: 16px;
`;

const SFamilyHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const SNameRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SFamilyName = styled.span`
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SOriginalName = styled.span`
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  white-space: nowrap;
`;

const SNameEditArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SNameInput = styled.input`
  height: 28px;
  padding: 0 8px;
  background: none;
  border: 1px solid var(--accent);
  border-radius: 4px;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  outline: none;
`;

const SIconBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  opacity: 0.5;
  transition: var(--transition-normal);
  &:hover {
    opacity: 1;
    background: var(--border);
  }
`;

/* PLAT-11412 — quiet caption-coverage note; muted italic, matching the
 * caption picker's "No {Script}" caveat treatment (accent means "selected"
 * on these surfaces, and the note informs rather than alarms). */
const SCoverageNote = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-style: italic;
`;

const SWeightRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SWeightChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--border);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const SWeightLabel = styled.span`
  line-height: 1;
  white-space: nowrap;
`;

const SDeleteVariantBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 2px;
  opacity: 0.6;
  transition: var(--transition-normal);
  &:hover {
    opacity: 1;
  }
`;

export default FontFamilyCard;
